import React, { useEffect, Suspense } from 'react';
import { Routes, Route, useLocation } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import { Toaster } from '@/components/ui/toaster';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import AdminAuthGuard from '@/components/AdminAuthGuard';
import { initPerformanceMonitoring, preloadCriticalResources } from '@/lib/performance';

// Static imports to eliminate eval usage - NO LAZY LOADING
import Home from '@/pages/Home';
import AboutUs from '@/pages/AboutUs';
import Booking from '@/pages/Booking';
import BookingNew from '@/pages/Booking/BookingNew';
import FreeEstimate from '@/pages/FreeEstimate';
import TestBooking from '@/pages/TestBooking';
import Contact from '@/pages/Contact';
import Fleet from '@/pages/Services';
import OurServices from '@/pages/OurServices';
import Reviews from '@/pages/Reviews';
import EmailMarketing from '@/pages/EmailMarketing';
import Admin from '@/pages/Admin';
import AdminLogin from '@/pages/AdminLogin';
import AdminDashboard from '@/pages/AdminDashboard';
import ResetPassword from '@/pages/Admin/ResetPassword';
import TestUpload from '@/pages/TestUpload';
import FileTestPage from '@/pages/FileTestPage';
import SimpleUploadTest from '@/pages/SimpleUploadTest';

// Component to conditionally render Header and Footer
const LayoutWrapper = ({ children }) => {
  const location = useLocation();
  const isAdminRoute = location.pathname.startsWith('/admin');
  
  return (
    <div 
      className="bg-brand-dark text-white min-h-screen flex flex-col"
      style={{ 
        display: 'flex', 
        flexDirection: 'column',
        fontFamily: 'Montserrat, sans-serif'
      }}
    >
      {!isAdminRoute && <Header />}
      <main className="flex-grow" style={{ flexGrow: 1 }}>
        {children}
      </main>
      {!isAdminRoute && <Footer />}
    </div>
  );
};

function App() {
  // Initialize performance monitoring and preload critical resources
  useEffect(() => {
    // Initialize performance monitoring (only in production)
    if (import.meta.env.PROD) {
      initPerformanceMonitoring();
      preloadCriticalResources();
    }
    
    // Register service worker only in production
    if ('serviceWorker' in navigator && import.meta.env.PROD) {
      navigator.serviceWorker.register('/sw.js')
        .then((registration) => {
          console.log('Service Worker registered successfully:', registration);
        })
        .catch((error) => {
          console.log('Service Worker registration failed:', error);
        });
    } else if (import.meta.env.DEV && 'serviceWorker' in navigator && navigator.serviceWorker) {
      // Unregister any existing service workers in development
      navigator.serviceWorker.getRegistrations().then((registrations) => {
        for (const registration of registrations) {
          registration.unregister();
          console.log('Service Worker unregistered for development');
        }
      });
    }
  }, []);

  return (
    <HelmetProvider>
      <LayoutWrapper>
        <Suspense fallback={
          <div 
            className="flex items-center justify-center min-h-screen"
            style={{ 
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'center', 
              minHeight: '100vh' 
            }}
          >
            <div 
              className="animate-spin rounded-full h-32 w-32 border-b-2 border-brand-gold"
              style={{ 
                animation: 'spin 1s linear infinite',
                borderRadius: '50%',
                height: '8rem',
                width: '8rem',
                borderBottom: '2px solid #D4AF37'
              }}
            ></div>
          </div>
        }>
          <Routes>
            <Route index element={<Home />} />
            <Route path="/about" element={<AboutUs />} />
            <Route path="/booking" element={<BookingNew />} />
            <Route path="/booking-old" element={<Booking />} />
            <Route path="/free-estimate" element={<FreeEstimate />} />
            <Route path="/test-booking" element={<TestBooking />} />
            <Route path="/contact" element={<Contact />} />
            <Route path="/fleet" element={<Fleet />} />
            <Route path="/services" element={<OurServices />} />
            <Route path="/reviews" element={<Reviews />} />
            <Route path="/email-marketing" element={<EmailMarketing />} />
            <Route path="/admin" element={
              <AdminAuthGuard>
                <Admin />
              </AdminAuthGuard>
            } />
            <Route path="/admin/login" element={<AdminLogin />} />
            <Route path="/admin/dashboard" element={
              <AdminAuthGuard>
                <AdminDashboard />
              </AdminAuthGuard>
            } />
            <Route path="/admin/reset-password" element={<ResetPassword />} />
            <Route path="/test-upload" element={<TestUpload />} />
            <Route path="/file-test" element={<FileTestPage />} />
            <Route path="/simple-upload" element={<SimpleUploadTest />} />
          </Routes>
        </Suspense>
        <Toaster />
      </LayoutWrapper>
    </HelmetProvider>
  );
}

export default App;